import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { communicationsApi } from '../lib/api'
import type {
  AddSuppressionRequest,
  CreateEmailRequest,
  EmailSourceRef,
  EmailSourceKind,
  PreviewRenderRequest,
  SaveTemplateRequest,
} from '../types/communications'

// L4 Communications hooks. Mirrors useContactList.ts's useQuery/useMutation shape (query-key
// namespacing, invalidation on mutate success, `enabled` gating on a possibly-absent id/source).

export function useEmailMessages(take = 100) {
  return useQuery({
    queryKey: ['communications', 'emails', take],
    queryFn: async () => {
      const response = await communicationsApi.list(take)
      return response.data
    },
  })
}

export function useEmailMessage(id: string | undefined) {
  return useQuery({
    queryKey: ['communications', 'emails', 'detail', id],
    queryFn: async () => {
      if (!id) throw new Error('Email message ID is required')
      const response = await communicationsApi.get(id)
      return response.data
    },
    enabled: !!id,
  })
}

/** Recipient table for the detail page (spec §8). GetRecipients (audited bulk-PII read) has no
 * skip/offset server-side -- like useSuppressions/useEmailMessages it's a capped `take`, not real
 * page-based pagination -- so this mirrors that same take-only shape rather than inventing a page
 * param the API can't honor. `enabled` defaults true but the caller (EmailDetailPage) gates it to
 * a terminal message status (Sent/Failed) so this never fires while a send is still in flight. */
export function useEmailRecipients(id: string | undefined, take = 200, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['communications', 'emails', 'recipients', id, take],
    queryFn: async () => {
      if (!id) throw new Error('Email message ID is required')
      const response = await communicationsApi.getRecipients(id, take)
      return response.data
    },
    enabled: (options?.enabled ?? true) && !!id,
  })
}

export function useRecipientPreview(source: EmailSourceRef | null, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['communications', 'recipient-preview', source],
    queryFn: async () => {
      if (!source) throw new Error('Source is required')
      const response = await communicationsApi.recipientPreview(source)
      return response.data
    },
    enabled: (options?.enabled ?? true) && !!source,
  })
}

/** POST .../preview renders one recipient's subject/html/text on demand (composer preview pane)
 * -- a mutation, not a query, since it's fired imperatively rather than kept in sync with a key. */
export function usePreviewRender() {
  return useMutation({
    mutationFn: (data: PreviewRenderRequest) =>
      communicationsApi.previewRender(data).then((r) => r.data),
  })
}

/** Enqueues the send (202 + {messageId, jobId, counts}) -- the message isn't actually sent yet.
 * Invalidates the ['communications','emails'] list so it picks up the new Queued row; the
 * caller is responsible for polling jobId (see jobsApi/useJobPolling) for completion, matching
 * useMaterializeList's precedent. */
export function useSendEmail() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateEmailRequest) =>
      communicationsApi.create(data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['communications', 'emails'] })
    },
  })
}

export function useEmailTemplates() {
  return useQuery({
    queryKey: ['communications', 'templates'],
    queryFn: async () => {
      const response = await communicationsApi.listTemplates()
      return response.data
    },
  })
}

/** Creates a template from the compose page's current subject+body ("Save as template").
 * Task 12's Templates page (list + edit drawer) reuses this same hook for its own
 * create flow rather than duplicating the mutation. */
export function useSaveTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: SaveTemplateRequest) =>
      communicationsApi.createTemplate(data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['communications', 'templates'] })
    },
  })
}

/** Task 12's Templates page edit-drawer "Save" for an existing template. Takes `{id, data}`
 * (not just `data`, like useSaveTemplate) since a mutation only gets one variable and updating
 * needs the target id alongside the new fields. */
export function useUpdateTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SaveTemplateRequest }) =>
      communicationsApi.updateTemplate(id, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['communications', 'templates'] })
    },
  })
}

export function useDeleteTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => communicationsApi.deleteTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['communications', 'templates'] })
    },
  })
}

export function useSuppressions(take?: number) {
  return useQuery({
    queryKey: ['communications', 'suppressions', take],
    queryFn: async () => {
      const response = await communicationsApi.listSuppressions(take)
      return response.data
    },
  })
}

/** Manual-suppression add (spec §7 `POST .../suppressions`) -- EmailSuppressionsPage's add form. */
export function useAddSuppression() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: AddSuppressionRequest) =>
      communicationsApi.addSuppression(data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['communications', 'suppressions'] })
    },
  })
}

/** Suppression removal (spec §7 `DELETE .../suppressions/{id}`) -- backend enforces the
 * Admin-only rule for Unsubscribed/Complained rows regardless of what the client shows; the
 * page only hides the control as UX (see EmailSuppressionsPage's `canRemove`). */
export function useRemoveSuppression() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => communicationsApi.removeSuppression(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['communications', 'suppressions'] })
    },
  })
}

export function useCommsStatus() {
  return useQuery({
    queryKey: ['communications', 'status'],
    queryFn: async () => {
      const response = await communicationsApi.status()
      return response.data
    },
  })
}

export function useMergeFields(source: EmailSourceKind | undefined) {
  return useQuery({
    queryKey: ['communications', 'merge-fields', source],
    queryFn: async () => {
      if (!source) throw new Error('Source is required')
      const response = await communicationsApi.mergeFields(source)
      return response.data
    },
    enabled: !!source,
  })
}
